import React from 'react';
import { FileText } from 'lucide-react';
import { SearchResult } from '../../types';
import { PromptCard } from './PromptCard';

interface SearchResultsProps {
  results: SearchResult[];
  onEdit: (prompt: any) => void;
  onDelete: (id: string) => void;
  onToggleFavorite: (id: string) => void;
  onGenerateVariations?: (prompt: any) => void;
  onEnhancePrompt?: (prompt: any) => void;
  onUpdatePrompt?: (id: string, updates: any, historyType?: 'enhanced' | 'manual' | 'variation') => void;
  onPromptCopy?: (prompt: any) => void; // New prop for tracking execution
  isBatchMode?: boolean;
  selectedPrompts?: Set<string>;
  onToggleSelection?: (id: string) => void;
  onDragStart?: (prompt: any) => void;
  onDragEnd?: () => void;
  draggedPromptId?: string | null;
  onMoveToFolder?: (promptId: string) => void;
}

export const SearchResults: React.FC<SearchResultsProps> = ({
  results,
  onEdit,
  onDelete,
  onToggleFavorite,
  onGenerateVariations,
  onEnhancePrompt,
  onUpdatePrompt,
  onPromptCopy,
  isBatchMode = false,
  selectedPrompts = new Set(),
  onToggleSelection,
  onDragStart,
  onDragEnd,
  draggedPromptId,
  onMoveToFolder,
}) => {
  const getMatchIcon = (matchType: string) => {
    switch (matchType) {
      case 'title':
        return <FileText className="h-4 w-4 text-blue-500" />;
      default:
        return <FileText className="h-4 w-4 text-gray-500" />;
    }
  };

  const getMatchLabel = (matchType: string) => {
    switch (matchType) {
      case 'title':
        return 'Title match';
      case 'body':
        return 'Content match';
      default:
        return 'Match';
    }
  };

  if (results.length === 0) {
    return (
      <div className="text-center py-12">
        <div className="text-gray-400 dark:text-gray-500 mb-4">
          <FileText className="h-12 w-12 mx-auto" />
        </div>
        <h3 className="text-lg font-medium text-gray-900 dark:text-dark-text mb-2">No results found</h3>
        <p className="text-gray-500 dark:text-gray-400">
          Try adjusting your search terms or using different keywords.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="text-xl font-semibold text-gray-900 dark:text-dark-text">
          Search Results ({results.length})
        </h2>
      </div>

      <div className="space-y-4">
        {results.map((result) => (
          <div key={result.prompt.id} className="space-y-2">
            <div className="flex items-center space-x-4 text-sm text-gray-600 dark:text-gray-400">
              <div className="flex items-center space-x-1">
                {getMatchIcon(result.matchType)}
                <span className="font-medium">{getMatchLabel(result.matchType)}</span>
              </div>
              
              {result.highlightedText && (
                <div 
                  className="text-gray-700 dark:text-gray-300 font-medium"
                  dangerouslySetInnerHTML={{ __html: result.highlightedText }}
                />
              )}
            </div>
            
            <PromptCard
              prompt={result.prompt}
              onEdit={onEdit}
              onDelete={onDelete}
              onToggleFavorite={onToggleFavorite}
              onGenerateVariations={onGenerateVariations}
              onEnhancePrompt={onEnhancePrompt}
              onUpdatePrompt={onUpdatePrompt}
              onPromptCopy={onPromptCopy}
              isBatchMode={isBatchMode}
              isSelected={selectedPrompts.has(result.prompt.id)}
              onToggleSelection={onToggleSelection}
              onDragStart={onDragStart}
              onDragEnd={onDragEnd}
              isDragging={draggedPromptId === result.prompt.id}
              onMoveToFolder={onMoveToFolder}
            />
          </div>
        ))}
      </div>
    </div>
  );
};